/**
 * Processor fees the GUEST pays. Tago never absorbs PayPal's cut.
 *
 * Card / Wompi already includes a 3.5% gross-up on Tago's 15% take
 * (`PLATFORM_FEES.guestServiceFeeProcessingPct`). That is the card total.
 *
 * PayPal charges a percentage of the *charged* amount plus a fixed fee.
 * We strip the Wompi-specific uplift, then gross up so Tago still nets the
 * marketplace total after PayPal takes its cut. Host payout is unchanged.
 *
 *   charge = ceil((net + fixed) / (1 - pct))
 *
 * MUST stay in lockstep with supabase/functions/_shared/processor-fee.ts
 * (re-export of this file via the same path create-payment already uses).
 */
export type CheckoutPaymentMethod = 'card' | 'paypal'

export const PAYPAL_PROCESSOR_FEE = {
  /** PayPal Commerce commercial rate for USD receipts, percent of charged amount. */
  pct: 4.99,
  /** Fixed per-transaction fee in cents (USD $0.49). */
  fixedCents: 49,
} as const

export function grossUpChargeCents(netCents: number, pct: number, fixedCents: number): number {
  const net = Math.max(0, Math.round(netCents))
  if (net <= 0) return 0
  const rate = pct / 100
  if (!(rate >= 0) || rate >= 1) {
    throw new Error('processor pct must be in [0, 100)')
  }
  const fixed = Math.max(0, Math.round(fixedCents))
  return Math.ceil((net + fixed) / (1 - rate))
}

export function guestChargeCents(params: {
  marketplaceCents: number
  method: CheckoutPaymentMethod
  /** Wompi uplift already inside marketplaceCents (0 for tours). */
  includedProcessorCents?: number
}): {
  marketplaceCents: number
  chargeCents: number
  processorFeeCents: number
} {
  const marketplaceCents = Math.max(0, Math.round(params.marketplaceCents))
  const included = Math.max(0, Math.round(params.includedProcessorCents ?? 0))

  if (params.method === 'card') {
    return {
      marketplaceCents,
      chargeCents: marketplaceCents,
      processorFeeCents: included,
    }
  }

  const net = Math.max(0, marketplaceCents - included)
  const chargeCents = grossUpChargeCents(
    net,
    PAYPAL_PROCESSOR_FEE.pct,
    PAYPAL_PROCESSOR_FEE.fixedCents,
  )
  return {
    marketplaceCents,
    chargeCents,
    processorFeeCents: chargeCents - net,
  }
}

/** Equal split of a marketplace total. Remainder cents go to the first share. */
export function splitMarketplaceCents(totalCents: number, participantCount: number): number[] {
  const n = Math.floor(participantCount)
  const total = Math.max(0, Math.round(totalCents))
  if (n < 2) return [total]
  const base = Math.floor(total / n)
  const remainder = total - base * n
  return Array.from({ length: n }, (_, index) => base + (index === 0 ? remainder : 0))
}
